import Vue from 'vue';

const configuredRoomLimit = parseInt(
    process.env.VUE_APP_CHAT_CACHE_ROOM_LIMIT,
    10
);

const MAX_CACHED_ROOMS =
    Number.isFinite(configuredRoomLimit) &&
    configuredRoomLimit > 0
        ? configuredRoomLimit
        : 30;

function normalizeMessages(messages) {
    return Array.isArray(messages) ? messages : [];
}

function cloneValue(value, seenValues) {
    if (!value || typeof value !== 'object') {
        return value;
    }

    const seen = seenValues || new WeakMap();

    if (seen.has(value)) {
        return seen.get(value);
    }

    if (Array.isArray(value)) {
        const clonedArray = [];
        seen.set(value, clonedArray);

        value.forEach((item) => {
            clonedArray.push(
                cloneValue(item, seen)
            );
        });

        return clonedArray;
    }

    if (value instanceof Date) {
        return new Date(value.getTime());
    }

    const clonedValue = {};
    seen.set(value, clonedValue);

    Object.keys(value).forEach((key) => {
        clonedValue[key] = cloneValue(
            value[key],
            seen
        );
    });

    return clonedValue;
}

function cloneMessage(message) {
    if (!message || typeof message !== 'object') {
        return message;
    }

    const cachedMessage = cloneValue(message);

    // Do not replay the live-message animation after restore.
    delete cachedMessage.is_new_message;

    return cachedMessage;
}

function cloneMessages(messages) {
    return normalizeMessages(messages).map(
        cloneMessage
    );
}

function cloneMessageListDto(dto) {
    if (!dto || typeof dto !== 'object') {
        return null;
    }

    const clonedDto = cloneValue(dto);
    clonedDto.results = cloneMessages(dto.results);

    return clonedDto;
}

function cloneCacheEntry(entry) {
    if (!entry || typeof entry !== 'object') {
        return null;
    }

    return {
        messages: cloneMessages(entry.messages),
        messageListResultsDto:
            cloneMessageListDto(
                entry.messageListResultsDto
            ),
        msgNextPageLoader:
            entry.msgNextPageLoader || null,
        cachedAt: entry.cachedAt || 0
    };
}

function getRoomKey(roomId) {
    if (
        roomId === undefined ||
        roomId === null ||
        roomId === ''
    ) {
        return '';
    }

    return String(roomId);
}

function createState() {
    return {
        roomsById: Object.create(null),
        // Oldest room first, newest room last.
        cachedRoomIds: []
    };
}

function markRoomAsRecentlyUsed(state, roomKey) {
    const existingIndex =
        state.cachedRoomIds.indexOf(roomKey);

    if (existingIndex !== -1) {
        state.cachedRoomIds.splice(
            existingIndex,
            1
        );
    }

    state.cachedRoomIds.push(roomKey);

    while (
        state.cachedRoomIds.length >
        MAX_CACHED_ROOMS
    ) {
        const oldestRoomId =
            state.cachedRoomIds.shift();

        Vue.delete(
            state.roomsById,
            oldestRoomId
        );
    }
}

export default {
    namespaced: true,

    state: createState,

    getters: {
        roomCacheById(state) {
            return (roomId) => {
                const roomKey =
                    getRoomKey(roomId);

                if (
                    !roomKey ||
                    !state.roomsById[roomKey]
                ) {
                    return null;
                }

                return cloneCacheEntry(
                    state.roomsById[roomKey]
                );
            };
        },

        hasRoomCache(state) {
            return (roomId) => {
                const roomKey =
                    getRoomKey(roomId);

                return Boolean(
                    roomKey &&
                    state.roomsById[roomKey]
                );
            };
        },

        cachedRoomIds(state) {
            return state.cachedRoomIds.slice();
        }
    },

    mutations: {
        SET_ROOM_CACHE(state, payload) {
            if (!payload) {
                return;
            }

            const roomKey =
                getRoomKey(payload.roomId);

            if (!roomKey) {
                return;
            }

            const cacheEntry = cloneCacheEntry({
                messages: payload.messages,
                messageListResultsDto:
                    payload.messageListResultsDto,
                msgNextPageLoader:
                    payload.msgNextPageLoader,
                cachedAt: Date.now()
            });

            Vue.set(
                state.roomsById,
                roomKey,
                cacheEntry
            );

            markRoomAsRecentlyUsed(
                state,
                roomKey
            );
        },

        TOUCH_ROOM_CACHE(state, roomId) {
            const roomKey = getRoomKey(roomId);

            if (
                !roomKey ||
                !state.roomsById[roomKey]
            ) {
                return;
            }

            markRoomAsRecentlyUsed(
                state,
                roomKey
            );
        },

        REMOVE_ROOM_CACHE(state, roomId) {
            const roomKey = getRoomKey(roomId);

            if (!roomKey) {
                return;
            }

            const roomIndex =
                state.cachedRoomIds.indexOf(
                    roomKey
                );

            Vue.delete(
                state.roomsById,
                roomKey
            );

            if (roomIndex !== -1) {
                state.cachedRoomIds.splice(
                    roomIndex,
                    1
                );
            }
        },

        CLEAR_CACHE(state) {
            Object.keys(
                state.roomsById
            ).forEach((roomKey) => {
                Vue.delete(
                    state.roomsById,
                    roomKey
                );
            });

            state.cachedRoomIds.splice(
                0,
                state.cachedRoomIds.length
            );
        }
    },

    actions: {
        cacheRoom({ commit }, payload) {
            commit(
                'SET_ROOM_CACHE',
                payload
            );
        },

        touchRoomCache({ commit }, roomId) {
            commit(
                'TOUCH_ROOM_CACHE',
                roomId
            );
        },

        removeRoomCache({ commit }, roomId) {
            commit(
                'REMOVE_ROOM_CACHE',
                roomId
            );
        },

        clearCache({ commit }) {
            commit('CLEAR_CACHE');
        }
    }
};
